import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type { FeedArticleProjectionData } from "@/data-access/shared/fragments/feed-article-projection-fragments";
import {
  createDiscoveryContentManagementActions,
  type ExcludeItemCallbacks,
  type ExcludeItemReasons,
} from "./discovery-content-management.actions";

export type { ExcludeItemCallbacks, ExcludeItemReasons };

// ============================================================================
// Broadcast Clips Table Slice
// ============================================================================

/** State for the Broadcast Clips tab table. */
export type DiscoveryBroadcastClipsTableSlice = {
  isFetchingBroadcastClips: boolean;
  isBroadcastClipsInitialLoading: boolean;
  broadcastClipsError: string | null;
  totalBroadcastClipsCount: number;
  broadcastClipsSearchTerm: string;

  setIsFetchingBroadcastClips: (isFetching: boolean) => void;
  setBroadcastClipsError: (error: string | null) => void;
  setTotalBroadcastClipsCount: (count: number) => void;
  setIsBroadcastClipsInitialLoading: (isLoading: boolean) => void;
  setBroadcastClipsSearchTerm: (searchTerm: string) => void;
  resetBroadcastClipsTableState: () => void;

  // Exclusion async actions
  excludeBroadcastClips: (
    feedId: string,
    reasons: ExcludeItemReasons,
    callbacks?: ExcludeItemCallbacks,
  ) => Promise<{ error: string | null }>;

  // Exclusion modal state
  isBroadcastExcludeModalOpen: boolean;
  pendingBroadcastExclusionIds: string[];
  setIsBroadcastExcludeModalOpen: (isOpen: boolean) => void;
  setPendingBroadcastExclusionIds: (ids: string[]) => void;
  resetBroadcastExcludeModalState: () => void;

  // Registered by BroadcastClipsTable so the store-connected modal can trigger
  // grid side-effects (deselect + purge cache) without holding a grid ref.
  broadcastExcludeSuccessCallback: (() => void) | null;
  setBroadcastExcludeSuccessCallback: (cb: (() => void) | null) => void;

  // Row selection (drives the bulk-delete button in the panel)
  selectedBroadcastClipIds: string[];
  setSelectedBroadcastClipIds: (ids: string[]) => void;
};

const initialBroadcastClipsTableState = {
  isFetchingBroadcastClips: false,
  isBroadcastClipsInitialLoading: true,
  broadcastClipsError: null,
  totalBroadcastClipsCount: 0,
  broadcastClipsSearchTerm: "",
  isBroadcastExcludeModalOpen: false,
  pendingBroadcastExclusionIds: [] as string[],
  broadcastExcludeSuccessCallback: null as (() => void) | null,
  selectedBroadcastClipIds: [] as string[],
};

// ============================================================================
// Podcast Episodes Table Slice
// ============================================================================

/** State for the Podcast Episodes tab table. */
export type DiscoveryPodcastEpisodesTableSlice = {
  isFetchingPodcastEpisodes: boolean;
  isPodcastEpisodesInitialLoading: boolean;
  podcastEpisodesError: string | null;
  totalPodcastEpisodesCount: number;
  podcastEpisodesSearchTerm: string;

  setIsFetchingPodcastEpisodes: (isFetching: boolean) => void;
  setPodcastEpisodesError: (error: string | null) => void;
  setTotalPodcastEpisodesCount: (count: number) => void;
  setIsPodcastEpisodesInitialLoading: (isLoading: boolean) => void;
  setPodcastEpisodesSearchTerm: (searchTerm: string) => void;
  resetPodcastEpisodesTableState: () => void;

  // Exclusion async actions
  excludePodcastEpisodes: (
    feedId: string,
    reasons: ExcludeItemReasons,
    callbacks?: ExcludeItemCallbacks,
  ) => Promise<{ error: string | null }>;

  // Exclusion modal state
  isPodcastExcludeModalOpen: boolean;
  pendingPodcastExclusionIds: string[];
  setIsPodcastExcludeModalOpen: (isOpen: boolean) => void;
  setPendingPodcastExclusionIds: (ids: string[]) => void;
  resetPodcastExcludeModalState: () => void;

  // Registered by PodcastEpisodesTable so the store-connected modal can trigger
  // grid side-effects (deselect + purge cache) without holding a grid ref.
  podcastExcludeSuccessCallback: (() => void) | null;
  setPodcastExcludeSuccessCallback: (cb: (() => void) | null) => void;

  // Row selection (drives the bulk-delete button in the panel)
  selectedPodcastEpisodeIds: string[];
  setSelectedPodcastEpisodeIds: (ids: string[]) => void;
};

const initialPodcastEpisodesTableState = {
  isFetchingPodcastEpisodes: false,
  isPodcastEpisodesInitialLoading: true,
  podcastEpisodesError: null,
  totalPodcastEpisodesCount: 0,
  podcastEpisodesSearchTerm: "",
  isPodcastExcludeModalOpen: false,
  pendingPodcastExclusionIds: [] as string[],
  podcastExcludeSuccessCallback: null as (() => void) | null,
  selectedPodcastEpisodeIds: [] as string[],
};

// ============================================================================
// Articles Table Slice
// ============================================================================

/**
 * Search state
 */
export interface SearchState {
  searchTerm: string;
  isAdvancedSearch: boolean;
}

/** Related to Articles Tab */
/**
 * Discovery content management slice
 */
export type DiscoveryArticlesTableSlice = {
  // Loading and error states
  isInitialLoading: boolean;
  isFetchingArticles: boolean;
  error: string | null;

  // Total count of articles (for overlay logic and display)
  totalCount: number;

  // Search functionality
  search: SearchState;

  // Data actions
  setInitialLoading: (loading: boolean) => void;
  setFetchingArticles: (fetching: boolean) => void;
  setError: (error: string | null) => void;
  setTotalCount: (totalCount: number) => void;
  setSearchTerm: (searchTerm: string) => void;
  setIsAdvancedSearch: (isAdvancedSearch: boolean) => void;
  resetState: () => void;
};

export type DiscoveryArticlesTableModalState = {
  isAddArticleModalOpen: boolean;
  /** True while the add-article subscription is in progress (for loading indicators). */
  isAddingArticle: boolean;
  /** When non-null, the preview-added-article modal is open with this article. */
  previewArticle: FeedArticleProjectionData | null;
  setIsAddArticleModalOpen: (isOpen: boolean) => void;
  setIsAddingArticle: (adding: boolean) => void;
  resetAddArticleModalState: () => void;
  setPreviewArticle: (article: FeedArticleProjectionData | null) => void;
};

/**
 * Initial search state
 */
const initialSearchState: SearchState = {
  searchTerm: "",
  isAdvancedSearch: false,
};

/**
 * Initial state
 */
const initialArticlesTableState = {
  isInitialLoading: false,
  isFetchingArticles: false,
  error: null,
  totalCount: 0,
  search: initialSearchState,
};

const initialArticlesTableModalState = {
  isAddArticleModalOpen: false,
  isAddingArticle: false,
  previewArticle: null as FeedArticleProjectionData | null,
};

/** Related to Narrative Clusters Table Tab */

export type DiscoveryNarrativeClustersTableSlice = {
  // Loading and error states
  isNarrativeClustersInitialLoading: boolean;
  isFetchingNarrativeClustersTableData: boolean;
  narrativeClustersTableError: string | null;

  // Total count of narrative clusters (for overlay logic and display)
  totalNarrativeClustersCount: number;

  // Search functionality
  narrativeClustersSearchTerm: string;

  // Data actions
  setIsNarrativeClustersInitialLoading: (loading: boolean) => void;
  setIsFetchingNarrativeClustersTableData: (fetching: boolean) => void;
  setNarrativeClustersTableError: (error: string | null) => void;
  setTotalNarrativeClustersCount: (totalCount: number) => void;
  setNarrativeClustersSearchTerm: (searchTerm: string) => void;
  resetNarrativeClustersTableState: () => void;
};

const initialNarrativeClustersTableState = {
  isNarrativeClustersInitialLoading: false,
  isFetchingNarrativeClustersTableData: false,
  narrativeClustersTableError: null,
  totalNarrativeClustersCount: 0,
  narrativeClustersSearchTerm: "",
};

const createDiscoveryContentManagement: StateCreator<
  DiscoveryArticlesTableSlice &
    DiscoveryNarrativeClustersTableSlice &
    DiscoveryArticlesTableModalState &
    DiscoveryBroadcastClipsTableSlice &
    DiscoveryPodcastEpisodesTableSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  ...initialArticlesTableState,
  ...initialNarrativeClustersTableState,
  ...initialArticlesTableModalState,
  ...initialBroadcastClipsTableState,
  ...initialPodcastEpisodesTableState,
  ...createDiscoveryContentManagementActions(set, get, api),
});

const useDiscoveryContentManagementStore = create<
  DiscoveryArticlesTableSlice &
    DiscoveryNarrativeClustersTableSlice &
    DiscoveryArticlesTableModalState &
    DiscoveryBroadcastClipsTableSlice &
    DiscoveryPodcastEpisodesTableSlice
>()(
  devtools(createDiscoveryContentManagement, {
    name: "discovery-content-management-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useDiscoveryContentManagementStore;
